import { useState } from 'react'

import { AuthContext, AuthState, destroyAuthSession, setAuthStateInSession } from './context'
import { SessionExpiryNotification } from './SessionExpiryNotification'

type Props = {
  initialAuthState: AuthState | null
  children: React.ReactNode | React.ReactNode[]
}

/**
 * This component provides the authentication state to its children via AuthContext.
 * It also manages the auto refresh of the session token on page load.
 *
 * Additionally it will show the "about to expire" notification if the session is about to expire.
 */
export const AuthProvider = ({ children, initialAuthState }: Props) => {
  const [authState, setAuthState] = useState<AuthState | null>(initialAuthState)

  // Re-sync context when the route loader revalidates auth (e.g. a session seeded after an
  // initial unauthenticated load). useState only captures the first value, so without this the
  // context stays stale-null while a guarded route's loader resolves a valid session, and
  // useRequiredAuth throws during render. Adjust during render (not useEffect) so the corrected
  // value is in place before children render. [RDM-FRONTEND-3KE]
  const [prevInitialAuthState, setPrevInitialAuthState] = useState(initialAuthState)
  if (initialAuthState !== prevInitialAuthState) {
    setPrevInitialAuthState(initialAuthState)
    setAuthState(initialAuthState)
  }

  const onUpdateSession = (newState: AuthState | null) => {
    setAuthState(newState)
    if (newState) {
      setAuthStateInSession(newState)
    } else {
      destroyAuthSession()
    }
  }

  return (
    <AuthContext value={authState}>
      {children}
      <SessionExpiryNotification onUpdateSession={onUpdateSession} />
    </AuthContext>
  )
}
